import { createContext, useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { getCustomerIsAuthenticated } from "../../utils/identity";
import { getCustomerSegmentIds } from "../../utils/customerSegments";
import { getHashedCustomerSegmentsKey } from "../../utils/getHashedCustomerSegmentsKey";
import getReturnsChargeMessaging from "../../api/returnsChargeMessagingApi";
import { useRuntimeConstants } from "../runtimeConstants/RuntimeConstantsContext";

const PersonalisedReturnsMessagesContext = createContext({
  isLoading: false,
  returnsChargeMessage: null,
});

export const PersonalisedReturnsMessagesProvider = ({ children }) => {
  const [personalisedReturnsMessages, setPersonalisedReturnsMessages] =
    useState({
      isLoading: false,
      returnsChargeMessage: null,
    });

  const {
    features: { personalisedReturnsMessageEnabled },
  } = useRuntimeConstants();

  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!personalisedReturnsMessageEnabled) {
      return;
    }

    const fetchPersonalisedReturnsMessages = async () => {
      const isAuthenticated = await getCustomerIsAuthenticated();
      if (!mountedRef.current) return;
      if (!isAuthenticated) {
        setPersonalisedReturnsMessages({
          isLoading: false,
          returnsChargeMessage: null,
        });
        return;
      }

      setPersonalisedReturnsMessages((prevState) => ({
        ...prevState,
        isLoading: true,
      }));
      let segmentIds;
      try {
        segmentIds = await getCustomerSegmentIds();
      } catch {
        if (mountedRef.current) {
          setPersonalisedReturnsMessages({
            isLoading: false,
            returnsChargeMessage: null,
          });
        }
        return;
      }
      if (!mountedRef.current) return;
      if (segmentIds) {
        try {
          const hashedCustomerSegmentsKey = await getHashedCustomerSegmentsKey(
            segmentIds,
          ).catch(() => null);
          const returnsChargeMessage = await getReturnsChargeMessaging(
            segmentIds,
            hashedCustomerSegmentsKey,
          );
          if (mountedRef.current) {
            setPersonalisedReturnsMessages({
              isLoading: false,
              returnsChargeMessage,
            });
          }
        } catch {
          if (mountedRef.current) {
            setPersonalisedReturnsMessages({
              isLoading: false,
              returnsChargeMessage: null,
            });
          }
        }
      } else {
        if (mountedRef.current) {
          setPersonalisedReturnsMessages({
            isLoading: false,
            returnsChargeMessage: null,
          });
        }
      }
    };

    fetchPersonalisedReturnsMessages();
  }, [personalisedReturnsMessageEnabled]);

  return (
    <PersonalisedReturnsMessagesContext.Provider
      value={personalisedReturnsMessages}
    >
      {children}
    </PersonalisedReturnsMessagesContext.Provider>
  );
};

PersonalisedReturnsMessagesProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export const usePersonalisedReturnsMessages = () =>
  useContext(PersonalisedReturnsMessagesContext);
